import { z } from 'zod';
import { type SemanticQueryRequest, semanticQueryRequestSchema } from './semantic-query.js';

/**
 * Explore boards — the persisted state of a BI canvas. Stored at
 * `users/{email}/exploreBoards/{boardId}`. Each board owns a list of
 * cells; each cell carries a semantic query + chart layout + display
 * options + optional Atlas-generated insight.
 *
 * Sharing model: when a board is shared, a `sharedBoards/{token}`
 * lookup doc is written pointing back to the owner + boardId. The token
 * itself is opaque (32 random chars) — anyone with the link reads the
 * board as read-only.
 *
 * Backward compatibility: `displayOptions` and `filters` are optional /
 * have defaults so boards persisted before the v2 schema land still
 * render with sensible defaults.
 */

const isoDateString = z.string().datetime();

/**
 * 16 chart types covering the canonical BI vocabulary. Recharts has
 * native primitives for all of these; the renderer dispatches by name.
 *
 *  - Comparison:   bar, stacked-bar, horizontal-bar
 *  - Trend:        line, area
 *  - Composition:  pie, donut, treemap, funnel
 *  - Relationship: scatter
 *  - Composite:    combo (bar + line overlay), dual-axis (two value axes)
 *  - Matrix:       heatmap (x×y intensity grid), pivot (cross-tab table)
 *  - Indicator:    kpi, big-number (KPI + sparkline), radial (gauge),
 *                  scorecard (value + progress toward a target — OKR widget)
 *  - Raw:          table
 *
 * `heatmap`, `dual-axis` and `pivot` are additive members never present on
 * pre-existing boards, so old docs keep parsing unchanged.
 */
export const exploreChartTypeSchema = z.enum([
  'line',
  'area',
  'bar',
  'stacked-bar',
  'horizontal-bar',
  'combo',
  'dual-axis',
  'scatter',
  'pie',
  'donut',
  'funnel',
  'treemap',
  'heatmap',
  'radial',
  'table',
  'pivot',
  'kpi',
  'big-number',
  'scorecard',
  // v5 additions (append-only — never reorder; old docs keep parsing).
  // Recharts-native: bubble, radar, sankey, waterfall, histogram, sunburst.
  'bubble',
  'radar',
  'sankey',
  'waterfall',
  'histogram',
  'sunburst',
  // Map family (deck.gl + maplibre, lazy-loaded). Geo fields live in
  // displayOptions.geo; absent fields are auto-detected from the result.
  'map-points',
  'map-bubble',
  'map-heat',
  'map-h3',
  'map-paths',
  'map-choropleth',
]);
export type ExploreChartType = z.infer<typeof exploreChartTypeSchema>;

/** Cell kinds. Boards persisted before v3 have no `kind` — they are all charts. */
export const exploreCellKindSchema = z.enum([
  'chart',
  'text',
  'header',
  'placeholder',
  'embed',
  'image',
  'divider',
  // v6 addition (append-only): an in-canvas filter control bound to the board
  // filter bar. Renders a dimension value picker or a time-range control that
  // reads/writes `board.filters` — the single source of truth (so cascade,
  // viewer-local state, the filter bar and Atlas suggest all keep working).
  'control',
]);
export type ExploreCellKind = z.infer<typeof exploreCellKindSchema>;

/**
 * Config for `control` cells. A control cell WITHOUT config renders its inline
 * setup editor (a valid authoring state — same convention as embed/image URLs,
 * never healed away). `dimension` is required at render for a 'dimension'
 * control; absent → the editor shows.
 */
export const exploreControlConfigSchema = z.object({
  controlType: z.enum(['dimension', 'time-range']),
  /** The categorical dimension this control filters (controlType 'dimension'). */
  dimension: z.string().min(1).max(120).optional(),
  /** Display label; absent → the dimension's field label / "Time range". */
  label: z.string().max(60).optional(),
  /** Multi-select (default) vs single-select value picker. */
  multi: z.boolean().optional(),
});
export type ExploreControlConfig = z.infer<typeof exploreControlConfigSchema>;

/** https-only URL for embed/image cells — mixed content would be blocked by
 *  the browser anyway, and http embeds would break on our https origin. */
const httpsUrlSchema = z
  .string()
  .url()
  .max(2_000)
  .refine((u) => u.startsWith('https://'), 'Only https URLs are allowed');

/** A horizontal goal/annotation line on cartesian charts. */
export const exploreAnnotationSchema = z.object({
  value: z.number().finite(),
  label: z.string().max(40).optional(),
  /** Absent → muted-foreground, same treatment as the target line. */
  color: z
    .string()
    .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Expected #RGB or #RRGGBB hex color')
    .optional(),
});
export type ExploreAnnotation = z.infer<typeof exploreAnnotationSchema>;

/**
 * OKR-lite per-cell target. Rendered by the indicator charts
 * (kpi / big-number / radial / scorecard) as progress toward the target
 * plus an on-target / near-target / off-target status.
 */
export const exploreCellTargetSchema = z.object({
  value: z.number().finite(),
  /** Short label shown instead of the metric name, e.g. "Q3 goal". */
  label: z.string().max(80).optional(),
  /** 'above' → good means actual >= value; 'below' → actual <= value. */
  direction: z.enum(['above', 'below']).default('above'),
  /** Attainment fraction below which the cell reads "off target". */
  warnAt: z.number().min(0).max(1).default(0.7),
});
/**
 * The three target verdicts. Declared as a schema (not just a TS union) because
 * the alerts cron persists the last verdict per cell and must validate it on read
 * like any other Firestore value. `lib/target-evaluation.ts` derives its
 * `TargetStatus` type from this, so there is one source of truth.
 */
export const targetStatusSchema = z.enum(['on-target', 'near-target', 'off-target']);

export type ExploreCellTarget = z.infer<typeof exploreCellTargetSchema>;

/**
 * The indicator family — the chart types that actually READ `target`/`compare`.
 *
 * Note the schema deliberately does not enforce this: `target` is optional on
 * every cell kind, so a persisted cell can carry one with `chartType: 'table'`
 * (and the trend renderers do use a target as a reference line). Anything that
 * EVALUATES targets headlessly — the alerts cron — must gate on this list, or it
 * would alert on cells that never displayed a status to anyone.
 */
export const INDICATOR_CHART_TYPES: readonly ExploreChartType[] = [
  'kpi',
  'big-number',
  'radial',
  'scorecard',
];

/**
 * Comparison mode. The frontend runs a second query with the board time
 * range shifted back by its own length — only for indicator chart types
 * (kpi / big-number / scorecard); enforcing that is a renderer policy so
 * a board-wide compare fan-out is never triggered from here.
 */
export const exploreCompareSchema = z.enum(['previous-period']);
export type ExploreCompare = z.infer<typeof exploreCompareSchema>;

/** #RGB or #RRGGBB. */
const hexColorSchema = z
  .string()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Expected #RGB or #RRGGBB hex color');

/** Named color palettes — full HEX swatches live in the frontend `palettes.ts`. */
export const explorePaletteSchema = z.enum([
  'kiwi',
  'corporate',
  'vibrant',
  'monochrome',
  'accessible',
  'sunset',
  'forest',
  'ocean',
  // A board-defined palette of arbitrary hex colors (see board.customPalette).
  'custom',
]);
export type ExplorePalette = z.infer<typeof explorePaletteSchema>;

/** Number format selector — applied to axis labels, KPIs, table cells, tooltips. */
export const exploreNumberFormatSchema = z.enum([
  'default', // 1,234.56
  'compact', // 1.2K, 3.4M
  'currency', // $1,234.56
  'percent', // 12.3%
  'integer', // 1,234
]);
export type ExploreNumberFormat = z.infer<typeof exploreNumberFormatSchema>;

/**
 * Date/time format selector for TIME-dimension axis labels, tooltips and table
 * cells. `auto` picks a sensible format from the dimension's grain (day →
 * "20 Jul", month → "Jul '26", quarter → "Q3 '26", year → "2026"); the rest are
 * explicit. Applied client-side via `formatDate` — never rewrites the query.
 */
export const exploreDateFormatSchema = z.enum([
  'auto', // grain-aware (see formatDate)
  'short', // 20 Jul
  'medium', // 20 Jul 2026
  'long', // 20 July 2026
  'month-year', // Jul '26
  'quarter-year', // Q3 '26
  'year', // 2026
  'iso', // 2026-07-20
  'time', // 14:30
  'datetime', // 20 Jul, 14:30
]);
export type ExploreDateFormat = z.infer<typeof exploreDateFormatSchema>;

/**
 * Board-wide display defaults ("settings generales"). Every key is optional and
 * NOT defaulted, so pre-existing boards round-trip byte-identical and old docs
 * keep parsing. Merge precedence at render time (resolved client-side in
 * `display-defaults.ts`):
 *   cell.displayOverrides (sparse) > cell.displayOptions (legacy full) >
 *   board settings baseline > system defaults.
 * So board settings govern every key a cell hasn't explicitly overridden.
 * Palette default lives on the existing `defaultPalette` field and auto-refresh
 * on `autoRefreshMinutes`; the settings panel surfaces both without duplicating.
 */
export const exploreBoardSettingsSchema = z.object({
  /** Default number format for cells that don't override it. */
  defaultNumberFormat: exploreNumberFormatSchema.optional(),
  /** Default date format for TIME-dimension labels in cells that don't override
   *  it. Absent → 'auto' (grain-aware) at render time. */
  defaultDateFormat: exploreDateFormatSchema.optional(),
  /** Board-wide legend / grid / card-chrome defaults. */
  showLegend: z.boolean().optional(),
  showGrid: z.boolean().optional(),
  showCellBackground: z.boolean().optional(),
  /** Default "show values" (data labels) for cells that don't override it. */
  defaultShowValues: z.boolean().optional(),
  /** Default decimal places (0-6) for cells that don't override it. */
  defaultDecimals: z.number().int().min(0).max(6).optional(),
  /** BCP-47 locale for number formatting board-wide (e.g. 'es-CO'). Inherited
   *  by cells that don't override it. */
  numberLocale: z.string().min(2).max(35).optional(),
  /** Chart entrance animation (maximize dialog / present overlay). Default on. */
  animation: z.boolean().optional(),
  /** Grid density: 'compact' → rowHeight 26 / margin 8; 'spacious' → 36 / 16
   *  (default 'comfortable' → 30 / 12). */
  density: z.enum(['comfortable', 'compact', 'spacious']).optional(),
  /** Canvas width. 'fixed' centers the canvas at a max width (focused,
   *  print-like); absent/'full' uses the whole width (the only pre-existing
   *  behavior). Board-scoped — applied directly, never per cell. */
  canvasWidth: z.enum(['full', 'fixed']).optional(),
  /** Cross-filter on click: clicking a categorical segment applies a view-only
   *  board filter on that value. `false` disables it board-wide. Absent → on
   *  (the pre-existing behavior). Board-scoped. */
  crossFilter: z.boolean().optional(),
});
export type ExploreBoardSettings = z.infer<typeof exploreBoardSettingsSchema>;

/**
 * Pivot / cross-tab config (chartType === 'pivot'). The same resolved rows are
 * reshaped client-side: `rows` dims become row groups, `cols` dims become
 * column groups, `value` is the measure aggregated by `agg`. No new server
 * query — it operates on the cell's existing query result.
 */
export const explorePivotConfigSchema = z.object({
  rows: z.array(z.string().min(1).max(200)).min(1).max(3),
  cols: z.array(z.string().min(1).max(200)).max(3).default([]),
  value: z.string().min(1).max(200),
  agg: z.enum(['sum', 'avg', 'count', 'min', 'max']).default('sum'),
  /** Grand-total column at the right of each row. Absent = off. */
  rowTotals: z.boolean().optional(),
  /** Grand-total row at the bottom of each column. Absent = off. */
  colTotals: z.boolean().optional(),
  /** Subtotal rows per first-row-dim group (needs >= 2 row dims). Absent = off. */
  subtotals: z.boolean().optional(),
});
export type ExplorePivotConfig = z.infer<typeof explorePivotConfigSchema>;

/**
 * Conditional formatting rule for table/pivot cells: when a numeric cell in
 * `column` satisfies the comparison, paint it `color`. Purely presentational,
 * evaluated client-side. Absent = no rules (current behavior).
 */
/** Basemap style for map charts — token-free Carto GL styles resolve these. */
export const exploreBasemapSchema = z.enum(['light', 'dark', 'streets']);
export type ExploreBasemap = z.infer<typeof exploreBasemapSchema>;

const geoFieldSchema = z.string().min(1).max(200);

/**
 * Map cell config (chartType 'map-*'). Absent fields are auto-detected from the
 * result columns at render time (lat/lng-ish names, H3-shaped strings,
 * WKT/GeoJSON strings). Everything optional so pre-existing docs round-trip
 * byte-identical.
 */
export const exploreGeoConfigSchema = z.object({
  latField: geoFieldSchema.optional(),
  lngField: geoFieldSchema.optional(),
  h3Field: geoFieldSchema.optional(),
  /** WKT or GeoJSON string column (map-choropleth). */
  geoField: geoFieldSchema.optional(),
  /** Numeric column driving the color ramp. Absent → first metric. */
  colorField: geoFieldSchema.optional(),
  /** Numeric column driving bubble radius (map-bubble). Absent → first metric. */
  sizeField: geoFieldSchema.optional(),
  /** Column grouping points into one path per value (map-paths). Absent → one
   *  path in row order. */
  pathGroupField: geoFieldSchema.optional(),
  /** Absent → follow the app theme. */
  basemap: exploreBasemapSchema.optional(),
});
export type ExploreGeoConfig = z.infer<typeof exploreGeoConfigSchema>;

export const exploreConditionalRuleSchema = z.object({
  column: z.string().min(1).max(200),
  op: z.enum(['gt', 'gte', 'lt', 'lte', 'eq', 'between']),
  value: z.number().finite(),
  /** Upper bound for `between`. */
  value2: z.number().finite().optional(),
  color: hexColorSchema,
  /** 'bg' tints the cell background; 'text' colors the number. */
  target: z.enum(['bg', 'text']).default('bg'),
});
export type ExploreConditionalRule = z.infer<typeof exploreConditionalRuleSchema>;

/** Y/X axis controls for single-Y-axis cartesian charts (line/area/bar/
 *  stacked-bar/combo/scatter). Whole object and every field optional with NO
 *  defaults so pre-existing docs round-trip byte-identical (same convention as
 *  seriesColors / annotations). */
export const exploreAxisOptionsSchema = z.object({
  yMin: z.number().finite().optional(),
  yMax: z.number().finite().optional(),
  logScale: z.boolean().optional(),
  xTitle: z.string().max(60).optional(),
  yTitle: z.string().max(60).optional(),
  /** Rotate X-axis tick labels (degrees, -90 to 0) so long/dense categories
   *  don't overlap or clip. Absent → 0 (horizontal). */
  xTickAngle: z.number().int().min(-90).max(0).optional(),
  /** X-axis tick density: 'all' forces a tick per category; 'auto' lets Recharts
   *  thin them (the pre-existing behavior). */
  xTickInterval: z.enum(['auto', 'all']).optional(),
  /** Y-axis width in px — widen so long value labels aren't clipped. Absent →
   *  Recharts auto width. */
  yWidth: z.number().int().min(20).max(240).optional(),
});
export type ExploreAxisOptions = z.infer<typeof exploreAxisOptionsSchema>;

/**
 * Analytics overlays for the trend chart family (line / area / combo): a linear
 * trend line, a moving-average line, and a short forward forecast with a ±σ
 * band. Pure client-side transforms over the already-loaded rows — never a new
 * query. Whole object + every field optional with NO defaults so pre-existing
 * docs round-trip byte-identical (same convention as axis / annotations). */
export const exploreAnalyticsSchema = z.object({
  /** Linear-regression trend line, one per plotted metric. */
  trend: z.boolean().optional(),
  /** Trailing moving-average line: window size in points (2-52). Absent → off. */
  movingAverage: z.number().int().min(2).max(52).optional(),
  /** Forward forecast: number of periods to project from a linear fit, with a
   *  ±1σ residual band. Needs a date-like X axis and ≥3 points; absent → off. */
  forecast: z.number().int().min(1).max(24).optional(),
});
export type ExploreAnalytics = z.infer<typeof exploreAnalyticsSchema>;

/**
 * Rich-tooltip config for cartesian charts (line/area/bar/stacked-bar/combo/
 * dual-axis/scatter/horizontal-bar). A custom tooltip that respects field
 * labels + per-column number format + decimals. Whole object + every field
 * optional so pre-existing docs render the current default Recharts tooltip
 * (absent = unchanged). */
export const exploreTooltipConfigSchema = z.object({
  /** 'default' → the current one-line-per-series tooltip; 'compact' → a tighter
   *  single block; 'detailed' → adds a Total row + per-series share. */
  style: z.enum(['default', 'compact', 'detailed']).default('default'),
  /** Restrict the tooltip to these series/column keys (RAW names). Absent → all
   *  plotted series (current behavior). */
  fields: z.array(z.string().min(1).max(200)).max(20).optional(),
});
export type ExploreTooltipConfig = z.infer<typeof exploreTooltipConfigSchema>;

export const exploreDisplayOptionsSchema = z.object({
  palette: explorePaletteSchema.default('kiwi'),
  /** Render the card chrome (border + background + shadow). false → the cell
   *  blends into the board (transparent, borderless) — Metabase-style. Default
   *  true so every pre-existing cell keeps its current look. */
  showBackground: z.boolean().default(true),
  showLegend: z.boolean().default(true),
  showGrid: z.boolean().default(true),
  showValues: z.boolean().default(false),
  /** Data-label density when showValues is on: all points / just the ends
   *  (first + last) / extremes (min + max) / only annotated highlights. Absent →
   *  'all' (current behavior). */
  labelMode: z.enum(['all', 'ends', 'extremes', 'highlight']).optional(),
  /** Data-label style: 'chip' draws a filled rounded background behind each value
   *  (Tableau-style highlight); 'plain' is bare text. Absent → 'plain'. */
  labelStyle: z.enum(['plain', 'chip']).optional(),
  smoothCurve: z.boolean().default(true),
  /** Stack bar/area series when applicable. Mutually exclusive with
   *  `stacked-bar` chart type which forces stacking. */
  stacked: z.boolean().default(false),
  /** 100%-stacked (normalized) — only meaningful when `stacked` is on. */
  normalized: z.boolean().default(false),
  numberFormat: exploreNumberFormatSchema.default('default'),
  /** Per-column number-format overrides: column/measure key → format. Wins
   *  over `numberFormat` for that column. No default so pre-existing docs
   *  round-trip byte-identical and unset columns keep using `numberFormat`. */
  columnFormats: z
    .record(z.string().min(1).max(200), exploreNumberFormatSchema)
    .refine((r) => Object.keys(r).length <= 30, 'At most 30 column format overrides')
    .optional(),
  /** Date/time format for TIME-dimension labels (axis / tooltip / table). No
   *  default so pre-existing docs round-trip byte-identical; absent → 'auto'
   *  (grain-aware) at render time. */
  dateFormat: exploreDateFormatSchema.optional(),
  /** Per-column date-format overrides: column key → date format. Wins over
   *  `dateFormat` for that column (table charts). Sibling of `columnFormats`. */
  columnDateFormats: z
    .record(z.string().min(1).max(200), exploreDateFormatSchema)
    .refine((r) => Object.keys(r).length <= 30, 'At most 30 column date-format overrides')
    .optional(),
  /** Per-series color overrides: series key → hex. Wins over `palette`.
   *  No default so pre-v3 docs round-trip byte-identical. */
  seriesColors: z
    .record(z.string().min(1).max(200), hexColorSchema)
    .refine((r) => Object.keys(r).length <= 12, 'At most 12 series color overrides')
    .optional(),
  /** Per-numeric-column background heat scale on table charts. Absent = off. */
  tableHeatmap: z.boolean().optional(),
  /** Totals row on table charts: aggregate every numeric column. Absent = off. */
  tableTotals: z.enum(['sum', 'avg']).optional(),
  /** Conditional-formatting rules for table/pivot cells. Absent = none. */
  conditionalFormats: z.array(exploreConditionalRuleSchema).max(10).optional(),
  /** Series/measure keys plotted on the RIGHT axis (chartType 'dual-axis').
   *  Absent → all series on the single left axis (current behavior). */
  rightAxisKeys: z.array(z.string().min(1).max(200)).max(8).optional(),
  /** Pivot config — read only when chartType === 'pivot'. Absent otherwise. */
  pivot: explorePivotConfigSchema.optional(),
  /** Map configuration — read only when chartType is a map type. Absent
   *  otherwise; unset fields are auto-detected from the result columns. */
  geo: exploreGeoConfigSchema.optional(),
  /** Histogram bin count (chartType 'histogram'). Absent → auto (Sturges,
   *  clamped 5–40). */
  histogramBins: z.number().int().min(2).max(100).optional(),
  /** Horizontal goal/annotation lines on cartesian charts. */
  annotations: z.array(exploreAnnotationSchema).max(5).optional(),
  /** Axis controls (bounds / log / titles) — cartesian charts only. Absent =
   *  auto everything (current behavior). */
  axis: exploreAxisOptionsSchema.optional(),
  /** Analytics overlays (trend / moving-average / forecast) — line/area/combo
   *  only. Absent = none (current behavior). See exploreAnalyticsSchema. */
  analytics: exploreAnalyticsSchema.optional(),
  /** Rich-tooltip config for cartesian charts. Absent = the default Recharts
   *  tooltip (current behavior). See exploreTooltipConfigSchema. */
  tooltipConfig: exploreTooltipConfigSchema.optional(),
  /** Decimal places for numeric display (0-6), overriding the format's built-in
   *  precision. Absent → the format's default (current behavior). */
  decimals: z.number().int().min(0).max(6).optional(),
  /** ISO-4217 currency code for the 'currency' format (e.g. 'USD','EUR','COP').
   *  Absent → 'USD' (the only pre-existing behavior). */
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/, 'Expected an ISO-4217 currency code (e.g. USD)')
    .optional(),
  /** BCP-47 locale for number formatting (e.g. 'en-US','es-CO'). Absent → the
   *  runtime default (current behavior). */
  numberLocale: z.string().min(2).max(35).optional(),
  /** Per-column decimal overrides: column/measure key → places (0-6). Wins over
   *  `decimals` for that column. Additive sibling of `columnFormats`. Absent →
   *  every column uses `decimals`/format default. */
  columnDecimals: z
    .record(z.string().min(1).max(200), z.number().int().min(0).max(6))
    .refine((r) => Object.keys(r).length <= 30, 'At most 30 column decimal overrides')
    .optional(),
});
export type ExploreDisplayOptions = z.infer<typeof exploreDisplayOptionsSchema>;

/**
 * Sparse per-cell display OVERRIDES — the heal-on-write successor to the full
 * `displayOptions` object. Same key set as `exploreDisplayOptionsSchema` but
 * EVERY key is `.optional()` with NO `.default()`, so an absent key means
 * "inherit the board theme" (resolved live in `display-defaults.ts`). This is
 * why board-wide "settings generales" finally govern: only the keys a user
 * explicitly changed vs the board baseline are persisted here. Optional + no
 * default on the cell field → legacy docs round-trip byte-identical.
 *
 * INVARIANT: the key set MUST mirror `exploreDisplayOptionsSchema` (guarded by
 * a parity test in `explore-board-schema.test.ts`).
 */
export const exploreDisplayOverridesSchema = z.object({
  palette: explorePaletteSchema.optional(),
  showBackground: z.boolean().optional(),
  showLegend: z.boolean().optional(),
  showGrid: z.boolean().optional(),
  showValues: z.boolean().optional(),
  labelMode: z.enum(['all', 'ends', 'extremes', 'highlight']).optional(),
  labelStyle: z.enum(['plain', 'chip']).optional(),
  smoothCurve: z.boolean().optional(),
  stacked: z.boolean().optional(),
  normalized: z.boolean().optional(),
  numberFormat: exploreNumberFormatSchema.optional(),
  columnFormats: z
    .record(z.string().min(1).max(200), exploreNumberFormatSchema)
    .refine((r) => Object.keys(r).length <= 30, 'At most 30 column format overrides')
    .optional(),
  dateFormat: exploreDateFormatSchema.optional(),
  columnDateFormats: z
    .record(z.string().min(1).max(200), exploreDateFormatSchema)
    .refine((r) => Object.keys(r).length <= 30, 'At most 30 column date-format overrides')
    .optional(),
  seriesColors: z
    .record(z.string().min(1).max(200), hexColorSchema)
    .refine((r) => Object.keys(r).length <= 12, 'At most 12 series color overrides')
    .optional(),
  tableHeatmap: z.boolean().optional(),
  tableTotals: z.enum(['sum', 'avg']).optional(),
  conditionalFormats: z.array(exploreConditionalRuleSchema).max(10).optional(),
  rightAxisKeys: z.array(z.string().min(1).max(200)).max(8).optional(),
  pivot: explorePivotConfigSchema.optional(),
  geo: exploreGeoConfigSchema.optional(),
  histogramBins: z.number().int().min(2).max(100).optional(),
  annotations: z.array(exploreAnnotationSchema).max(5).optional(),
  axis: exploreAxisOptionsSchema.optional(),
  analytics: exploreAnalyticsSchema.optional(),
  tooltipConfig: exploreTooltipConfigSchema.optional(),
  decimals: z.number().int().min(0).max(6).optional(),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/, 'Expected an ISO-4217 currency code (e.g. USD)')
    .optional(),
  numberLocale: z.string().min(2).max(35).optional(),
  columnDecimals: z
    .record(z.string().min(1).max(200), z.number().int().min(0).max(6))
    .refine((r) => Object.keys(r).length <= 30, 'At most 30 column decimal overrides')
    .optional(),
});
export type ExploreDisplayOverrides = z.infer<typeof exploreDisplayOverridesSchema>;

/**
 * Time range filter applied across the whole board (or, via a cell's
 * `timeOverride`, to a single cell). The filter is translated to a MetricFlow
 * `where` clause by the frontend at query time, never persisted as a literal
 * where-string (so the dates stay tied to "now" when the user picks a preset).
 */
export const exploreTimeRangePresetSchema = z.enum([
  'last-7d',
  'last-30d',
  'last-90d',
  'last-365d',
  'mtd',
  'qtd',
  'ytd',
  'last-month',
  'last-quarter',
  'last-year',
  'custom',
]);
export type ExploreTimeRangePreset = z.infer<typeof exploreTimeRangePresetSchema>;

export const exploreTimeRangeSchema = z.object({
  preset: exploreTimeRangePresetSchema.default('last-90d'),
  /** Required when preset === 'custom'. Inclusive ISO dates. */
  from: z.string().date().optional(),
  to: z.string().date().optional(),
  /** Granularity used in the MetricFlow `TimeDimension` call. */
  grain: z.enum(['day', 'week', 'month', 'quarter', 'year']).default('day'),
  /** Time dim base name — almost always `metric_time` upstream. */
  timeDimension: z.string().default('metric_time'),
});
export type ExploreTimeRange = z.infer<typeof exploreTimeRangeSchema>;

/**
 * Grid-coordinate field that **clamps instead of rejecting**. react-grid-layout
 * (especially older mobile reflows) can emit out-of-range w/h (e.g. w=1 on a
 * 1-column phone layout). Rejecting would 400 the whole board's autosave and
 * orphan it; clamping heals the cell on the next read/write. Rounds too, so a
 * fractional value never slips through.
 */
function clampedGridInt(min: number, max: number) {
  return z.number().transform((n) => Math.min(max, Math.max(min, Math.round(n))));
}

const exploreCellBaseSchema = z.object({
  /** Stable cell id (UUID). Used as react-grid-layout's `i` key. */
  id: z.string().min(1).max(64),
  /** Grid coords, 12-column layout. Clamped (not rejected) — see clampedGridInt. */
  x: clampedGridInt(0, 12),
  y: clampedGridInt(0, 200),
  w: clampedGridInt(2, 12),
  h: clampedGridInt(2, 40),
  /** User-editable title; the section heading for `header` cells. */
  title: z.string().max(120).default(''),
  /** Cell kind. Absent on pre-v3 docs → 'chart'. */
  kind: exploreCellKindSchema.default('chart'),
  /** Owning tab id (when the board uses tabs). Optional (NOT defaulted) so
   *  pre-tab cells round-trip byte-identical; an undefined/orphan tabId resolves
   *  to the first tab at render time, so no cell ever disappears. */
  tabId: z.string().min(1).max(64).optional(),
  /** The semantic query a `chart` cell renders. Absent for text/header/
   *  placeholder kinds — see the healing transform below for chart cells. */
  query: semanticQueryRequestSchema.optional(),
  /** Markdown body for `text` cells (rendered with streamdown). */
  text: z.string().max(4_000).optional(),
  /** Embedded page URL for `embed` cells. Absent → the cell renders its URL
   *  editor (a valid authoring state — never healed away). */
  embedUrl: httpsUrlSchema.optional(),
  /** Image URL for `image` cells. */
  imageUrl: httpsUrlSchema.optional(),
  /** object-fit for image cells. Absent → 'contain' (component-side default
   *  so old/new docs round-trip unchanged). */
  imageFit: z.enum(['cover', 'contain']).optional(),
  /** Config for `control` cells. Absent → the cell renders its inline setup
   *  editor (a valid authoring state, like embed/image URLs). */
  control: exploreControlConfigSchema.optional(),
  /** Per-cell time range override. When present, this cell IGNORES the board
   *  timeRange and uses this one (categorical board filters still apply).
   *  Absent → follow the board (the only pre-v4 behavior). */
  timeOverride: exploreTimeRangeSchema.optional(),
  /** Meaningless for non-chart kinds; defaulted so they parse without it.
   *  Pre-v3 docs always carry an explicit value. A placeholder stores the
   *  chart type it converts to when a metric is dropped on it. */
  chartType: exploreChartTypeSchema.default('table'),
  /** OKR-lite target — read by indicator renderers only. */
  target: exploreCellTargetSchema.optional(),
  /** Period-over-period comparison flag (indicator renderers only). */
  compare: exploreCompareSchema.optional(),
  /** Per-cell display options — palette, formatting, toggles. LEGACY full
   *  object (materializes all defaults). New/edited cells persist the sparse
   *  `displayOverrides` instead; see resolveCellDisplay. Kept for round-trip. */
  displayOptions: exploreDisplayOptionsSchema.optional(),
  /** Sparse per-cell display overrides (heal-on-write successor to
   *  `displayOptions`). Only keys that differ from the board theme are stored,
   *  so board settings govern every unset key. See `exploreDisplayOverridesSchema`. */
  displayOverrides: exploreDisplayOverridesSchema.optional(),
  /** Cached Atlas insight, if generated. Not regenerated on read. */
  insight: z
    .object({
      summary: z.string().max(2_000),
      keyFindings: z.array(z.string().max(500)).max(5),
      followUps: z.array(z.string().max(280)).max(3),
      generatedAt: isoDateString,
    })
    .optional(),
});

/**
 * Healing invariant: a `chart` cell without a query can't render — instead of
 * failing the parse (which would drop the WHOLE board on read: hydrate() in
 * the API is drop-on-fail at board granularity), demote it to a placeholder
 * slot. Mirrors the clampedGridInt philosophy: heal, don't orphan.
 *
 * Note: the transform makes this a ZodEffects — don't `.extend()`/`.partial()`
 * it downstream; compose from `exploreCellBaseSchema` shapes instead.
 */
export const exploreCellSchema = exploreCellBaseSchema.transform((cell) =>
  cell.kind === 'chart' && cell.query === undefined
    ? { ...cell, kind: 'placeholder' as const }
    : cell,
);
export type ExploreCell = z.infer<typeof exploreCellSchema>;

/** Narrowing guard for renderers/heuristics that need a non-null query. */
export function isChartCell(
  cell: ExploreCell,
): cell is ExploreCell & { query: SemanticQueryRequest } {
  return cell.kind === 'chart' && cell.query !== undefined;
}

export const exploreCategoricalFilterSchema = z.object({
  dimension: z.string().min(1).max(120),
  // May be empty: a filter just added (value not picked yet) persists harmlessly
  // and `applyBoardFilters` skips it (no `IN ()`). Existing filters have ≥1.
  values: z.array(z.string().min(1).max(200)).max(50).default([]),
  /** When true, treat as exclude (NOT IN). Default include (IN). */
  exclude: z.boolean().default(false),
  /** UI hint for the filter editor: 'multiselect' shows a searchable
   *  value picker (distinct values derived from loaded data), 'text' the
   *  free-text entry. Absent → current free-text behavior. */
  filterType: z.enum(['multiselect', 'text']).optional(),
  /** Linked/cascade: the dimension of the PARENT filter this one depends on.
   *  When set, the value picker only offers values that co-occur with the
   *  parent's current selection (e.g. campus → robots). Purely a picker-time
   *  narrowing — the applied `where` is unchanged. Absent → independent. */
  dependsOn: z.string().min(1).max(120).optional(),
});
export type ExploreCategoricalFilter = z.infer<typeof exploreCategoricalFilterSchema>;

export const exploreBoardFiltersSchema = z.object({
  timeRange: exploreTimeRangeSchema.optional(),
  categorical: z.array(exploreCategoricalFilterSchema).max(10).default([]),
  /** Raw MetricFlow where-expressions (Atlas-suggested, user-confirmed).
   *  Merged verbatim into every cell's `where` at query time. */
  rawWhere: z.array(z.string().min(1).max(280)).max(6).default([]),
});
export type ExploreBoardFilters = z.infer<typeof exploreBoardFiltersSchema>;

/** One tab within a board. A board with `tabs: []` has no tab bar (every
 *  pre-existing board); when tabs exist, each cell belongs to a tab via its
 *  `tabId`, falling back to the first tab when unset/orphaned. */
export const exploreBoardTabSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(60),
  order: z.number().int().min(0),
  /** Presenter-only notes for this tab — the speaker's script, shown in present
   *  mode behind `n` and NEVER projected to the audience (that is
   *  `PresentNarration`, built from the Atlas breakdown). Optional with NO
   *  default so pre-existing boards round-trip byte-identically. `tabs` is
   *  already classified as a CONTENT patch key, so editors can write it and
   *  viewers 403 — no API change. */
  notes: z.string().max(2_000).optional(),
});
export type ExploreBoardTab = z.infer<typeof exploreBoardTabSchema>;

export const exploreBoardCollaboratorRoleSchema = z.enum(['editor', 'viewer']);
export type ExploreBoardCollaboratorRole = z.infer<typeof exploreBoardCollaboratorRoleSchema>;

/** One named collaborator. Emails are ALWAYS stored lowercased (server enforces). */
export const exploreBoardCollaboratorSchema = z.object({
  email: z.string().email().max(120),
  role: exploreBoardCollaboratorRoleSchema,
});
export type ExploreBoardCollaborator = z.infer<typeof exploreBoardCollaboratorSchema>;

/** Resolved access level for the calling user (owner > editor > viewer). */
export const exploreBoardAccessRoleSchema = z.enum(['owner', 'editor', 'viewer']);
export type ExploreBoardAccessRole = z.infer<typeof exploreBoardAccessRoleSchema>;

/** Per-board display labels for semantic fields (metrics + dimensions), keyed
 *  by RAW catalog name. Display-only — never rewrites queries, drag payloads,
 *  seriesColors/columnFormats keys, or row values. Optional + NO default so
 *  legacy Firestore docs round-trip byte-identical (seriesColors convention). */
export const exploreBoardFieldLabelsSchema = z
  .record(z.string().min(1).max(200), z.string().min(1).max(120))
  .refine((rec) => Object.keys(rec).length <= 50, {
    message: 'At most 50 field labels per board',
  });
export type ExploreBoardFieldLabels = z.infer<typeof exploreBoardFieldLabelsSchema>;

/**
 * Atlas "Living Breakdown" — a persisted, per-tab narrative of the board,
 * generated by the read-only `generateBoardBreakdown` tool and ASSEMBLED +
 * persisted by the client (same pattern as Generative Apps: the tool returns
 * only prose; the client stamps the numbers/basis and PATCHes the board). This
 * is a Firestore-input schema, so `.default([])` on arrays is fine (unlike Atlas
 * OUTPUT schemas). The whole `breakdown` field is optional with NO default, so
 * pre-existing boards round-trip byte-identical.
 *
 * `basisHash` on each level lets the client detect staleness (see
 * `breakdown-basis.ts`): when a cell's query/chart/data changes, its hash drifts
 * and the tab/board reads "out of date" until regenerated.
 */
export const exploreBreakdownHighlightSchema = z.object({
  label: z.string().min(1).max(120),
  /** PRE-FORMATTED display string (e.g. "$1.2M", "+14%"). Numbers are
   *  client-owned — the LLM never emits raw values here. */
  value: z.string().min(1).max(120),
});
export type ExploreBreakdownHighlight = z.infer<typeof exploreBreakdownHighlightSchema>;

export const exploreBreakdownCellSchema = z.object({
  cellId: z.string().min(1).max(64),
  title: z.string().max(120),
  chartType: exploreChartTypeSchema.optional(),
  narrative: z.string().max(2_000),
  highlights: z.array(exploreBreakdownHighlightSchema).max(6).default([]),
  basisHash: z.string().min(1).max(64),
});
export type ExploreBreakdownCell = z.infer<typeof exploreBreakdownCellSchema>;

export const exploreBreakdownSectionSchema = z.object({
  /** Owning tab (absent → the single implicit view of an untabbed board). */
  tabId: z.string().min(1).max(64).optional(),
  tabName: z.string().max(60).optional(),
  narrative: z.string().max(2_000),
  cells: z.array(exploreBreakdownCellSchema).max(50).default([]),
  basisHash: z.string().min(1).max(64),
  generatedAt: isoDateString,
});
export type ExploreBreakdownSection = z.infer<typeof exploreBreakdownSectionSchema>;

export const exploreBoardBreakdownSchema = z.object({
  overview: z.string().max(4_000),
  keyFindings: z.array(z.string().min(1).max(500)).max(8).default([]),
  sections: z.array(exploreBreakdownSectionSchema).max(20).default([]),
  /** Human summary of the filters in effect when generated (e.g. "Last 90 days,
   *  Campus = Berkeley"). Absent → no active filters. */
  filterContext: z.string().max(500).optional(),
  /** Hash of the whole board basis at generation time — the board-level staleness
   *  signal (a cheap "anything changed?" check before diffing per-cell). */
  basisHash: z.string().min(1).max(64),
  /** Monotonic bump each regeneration — lets the UI tell "newer" from "older"
   *  without parsing timestamps. */
  basisRevision: z.number().int().min(0).optional(),
  generatedAt: isoDateString,
  generatedBy: z.string().email().optional(),
  /** Atlas model/version stamp for provenance. */
  atlasVersion: z.string().max(40).optional(),
});
export type ExploreBoardBreakdown = z.infer<typeof exploreBoardBreakdownSchema>;

export const exploreBoardSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(120),
  ownerEmail: z.string().email(),
  /** Optional human description shown on the landing card + present mode. */
  description: z.string().max(500).optional(),
  /** Pinned boards sort first on the landing page. */
  pinned: z.boolean().default(false),
  /** Favorited boards get a dedicated section on the landing page. Distinct
   *  from `pinned` (which only affects sort) — a board can be either, both, or
   *  neither. Default false keeps pre-existing boards out of the section. */
  favorite: z.boolean().default(false),
  /** Tabs within the board. Empty → single implicit view, no tab bar (every
   *  pre-existing board). Default [] keeps old docs untabbed. */
  tabs: z.array(exploreBoardTabSchema).max(20).default([]),
  /** Collection this board lives under on the home, or absent/null = root.
   *  Nullable+optional + NOT defaulted so legacy boards (absent) stay at root
   *  and a move-to-root PATCH sends `null` (the merge can't delete keys). */
  collectionId: z.string().min(1).max(64).nullable().optional(),
  cells: z.array(exploreCellSchema).max(50).default([]),
  /** Token for read-only public links. Absent → not shared. */
  shareToken: z.string().min(16).max(64).optional(),
  /** Named collaborators (owner excluded). Managed ONLY via
   *  PUT /:id/collaborators — deliberately absent from the PATCH schema.
   *  Default [] keeps legacy docs parsing. */
  collaborators: z.array(exploreBoardCollaboratorSchema).max(20).default([]),
  /** Denormalized lowercase emails for array-contains queries ("shared with
   *  me", cross-user GET). ALWAYS derived server-side from `collaborators` —
   *  never trusted from a client. Default [] keeps legacy docs parsing. */
  collaboratorEmails: z.array(z.string().email()).max(20).default([]),
  /** Who last saved content — names the "board was updated by X" 409 toast. */
  lastEditedBy: z.string().email().optional(),
  /** Board-wide filters applied to every cell at query time. */
  filters: exploreBoardFiltersSchema.optional(),
  /** Default palette new cells inherit. Cells can override per-cell. */
  defaultPalette: explorePaletteSchema.optional(),
  /** Board-defined custom palette (hex[]) — the colors used when a palette is
   *  'custom'. Optional + no default so legacy boards round-trip byte-identical. */
  customPalette: z.array(hexColorSchema).min(1).max(12).optional(),
  /** Board-wide display defaults ("settings generales"). Optional so pre-existing
   *  boards round-trip unchanged; cells without their own displayOptions inherit. */
  settings: exploreBoardSettingsSchema.optional(),
  /** Per-board field renames (raw name → display label). See
   *  exploreBoardFieldLabelsSchema. */
  fieldLabels: exploreBoardFieldLabelsSchema.optional(),
  /** Atlas "Living Breakdown" — persisted narrative of the board (per tab).
   *  Optional + no default so legacy boards round-trip byte-identical. */
  breakdown: exploreBoardBreakdownSchema.optional(),
  /** Auto-refresh cadence in minutes (0 = off). Persisted per board so a
   *  monitoring/shared board keeps refreshing for any viewer. Default 0 keeps
   *  pre-existing boards static. */
  autoRefreshMinutes: z.number().int().min(0).max(60).default(0),
  /** When the last version snapshot was taken (throttles auto-snapshots). */
  lastVersionAt: isoDateString.optional(),
  createdAt: isoDateString,
  updatedAt: isoDateString,
});
export type ExploreBoard = z.infer<typeof exploreBoardSchema>;

/** Body for `POST /metric-hub/explore-boards`. Accepts the full board content
 *  so templates and client-side board duplication ride the same endpoint.
 *  `pinned` is deliberately absent — a copy never inherits pinned status. */
export const exploreBoardCreateSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  cells: z.array(exploreCellSchema).max(50).optional(),
  tabs: z.array(exploreBoardTabSchema).max(20).optional(),
  filters: exploreBoardFiltersSchema.optional(),
  defaultPalette: explorePaletteSchema.optional(),
  customPalette: z.array(hexColorSchema).min(1).max(12).optional(),
  settings: exploreBoardSettingsSchema.optional(),
  /** Duplication carries field renames over to the copy. */
  fieldLabels: exploreBoardFieldLabelsSchema.optional(),
});
export type ExploreBoardCreate = z.infer<typeof exploreBoardCreateSchema>;

/** Body for `PATCH /metric-hub/explore-boards/:id`. The route merges
 *  `{...existing, ...patch}` so a key can't be deleted — send `''` to clear
 *  `description` (same pre-existing limitation as `filters`). */
export const exploreBoardUpdateSchema = z
  .object({
    name: z.string().min(1).max(120),
    cells: z.array(exploreCellSchema).max(50),
    filters: exploreBoardFiltersSchema,
    defaultPalette: explorePaletteSchema,
    customPalette: z.array(hexColorSchema).min(1).max(12),
    settings: exploreBoardSettingsSchema,
    description: z.string().max(500),
    pinned: z.boolean(),
    favorite: z.boolean(),
    tabs: z.array(exploreBoardTabSchema).max(20),
    /** `null` moves the board back to root (the merge can't delete keys). */
    collectionId: z.string().min(1).max(64).nullable(),
    autoRefreshMinutes: z.number().int().min(0).max(60),
    /** Full replacement record — the merge can't delete keys, so clearing a
     *  rename means re-sending the record without that key ({} is valid). */
    fieldLabels: exploreBoardFieldLabelsSchema,
    /** Atlas Living Breakdown — PATCHed by the client after generation (editors
     *  only; classified as CONTENT so viewers 403 and token GET sees it). */
    breakdown: exploreBoardBreakdownSchema,
    /** Optimistic-concurrency precondition: the board's updatedAt the client
     *  last saw. Mismatch → 409 conflict. Sent only by content autosaves.
     *  NEVER persisted to the doc (stripped server-side before the write). */
    baseUpdatedAt: isoDateString,
  })
  .partial();
export type ExploreBoardUpdate = z.infer<typeof exploreBoardUpdateSchema>;

/** Body for `PUT /:id/collaborators` — the full replacement collaborator list. */
export const exploreBoardCollaboratorsUpdateSchema = z.object({
  collaborators: z.array(exploreBoardCollaboratorSchema).max(20),
});
export type ExploreBoardCollaboratorsUpdate = z.infer<typeof exploreBoardCollaboratorsUpdateSchema>;

/** Body for `POST /:id/transfer` — hand ownership to another user. */
export const exploreBoardTransferSchema = z.object({
  toEmail: z.string().email().max(120),
});
export type ExploreBoardTransfer = z.infer<typeof exploreBoardTransferSchema>;

/** Shape of `sharedBoards/{token}` lookup docs (replaces a raw `as` cast). */
export const sharedBoardTokenDocSchema = z.object({
  ownerEmail: z.string().email(),
  boardId: z.string().min(1).max(64),
});
export type SharedBoardTokenDoc = z.infer<typeof sharedBoardTokenDocSchema>;

/** Compact cell footprint for landing-page thumbnails — geometry only. */
export const exploreCellPreviewSchema = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  kind: exploreCellKindSchema.optional(),
  chartType: exploreChartTypeSchema.optional(),
});
export type ExploreCellPreview = z.infer<typeof exploreCellPreviewSchema>;

/** Shape returned by `GET /metric-hub/explore-boards` (list). */
export const exploreBoardSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  cellCount: z.number().int().nonnegative(),
  updatedAt: isoDateString,
  shared: z.boolean(),
  pinned: z.boolean().default(false),
  favorite: z.boolean().default(false),
  /** Collection grouping for the home (absent/null = root). */
  collectionId: z.string().nullable().optional(),
  /** Lowercased haystack for landing-page full-text search — board name +
   *  description + each cell's title / metrics / group-by dims / text. Built
   *  server-side via `buildBoardSearchText`; capped so the list stays small. */
  searchText: z.string().default(''),
  /** First 30 cell footprints, for the layout-mosaic thumbnail. */
  cellsPreview: z.array(exploreCellPreviewSchema).max(30).default([]),
  /** Present only on /shared-with-me items — the board's owner. */
  ownerEmail: z.string().email().optional(),
  /** Present only on /shared-with-me items — the caller's access level. */
  accessRole: exploreBoardAccessRoleSchema.optional(),
});
export type ExploreBoardSummary = z.infer<typeof exploreBoardSummarySchema>;

/** Minimal cell shape `buildBoardSearchText` reads — a real `ExploreCell`
 *  satisfies it. Kept structural so the helper has no heavy dependency. */
interface BoardSearchCell {
  title?: string;
  text?: string;
  query?: { metrics?: string[]; group_by?: string[] };
}

/**
 * Build the lowercased full-text haystack for a board: name + description +
 * each cell's title, metrics, group-by dimensions and text body. Phrases are
 * deduped (cells repeat metric names) and the result is capped at 1000 chars
 * so 100 boards stay a small list payload.
 */
export function buildBoardSearchText(board: {
  name: string;
  description?: string;
  cells: BoardSearchCell[];
  tabs?: { name: string }[];
}): string {
  const parts: string[] = [board.name];
  if (board.description) parts.push(board.description);
  for (const tab of board.tabs ?? []) parts.push(tab.name);
  for (const cell of board.cells) {
    if (cell.title) parts.push(cell.title);
    if (cell.text) parts.push(cell.text);
    if (cell.query?.metrics) parts.push(...cell.query.metrics);
    if (cell.query?.group_by) parts.push(...cell.query.group_by);
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parts) {
    const t = p.trim().toLowerCase();
    if (t && !seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
  }
  return out.join(' ').slice(0, 1000);
}

/**
 * Full-text match for the landing page: every whitespace-separated term in
 * `query` must appear in `searchText` (case-insensitive AND). Empty query
 * matches everything. `searchText` is assumed already lowercased.
 */
export function boardMatchesSearch(searchText: string, query: string): boolean {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  return terms.every((t) => searchText.includes(t));
}

/**
 * Board version snapshots — `users/{email}/exploreBoards/{id}/versions/{vid}`.
 * Written server-side: auto (throttled, on content PATCHes), manual ("Save
 * version"), and pre-restore (frozen current state before a restore, so a
 * restore is always reversible). Retention: last 20.
 */
export const exploreBoardVersionTriggerSchema = z.enum(['auto', 'manual', 'pre-restore']);
export type ExploreBoardVersionTrigger = z.infer<typeof exploreBoardVersionTriggerSchema>;

export const exploreBoardVersionSchema = z.object({
  id: z.string().min(1).max(120),
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  cells: z.array(exploreCellSchema).max(50).default([]),
  tabs: z.array(exploreBoardTabSchema).max(20).default([]),
  filters: exploreBoardFiltersSchema.optional(),
  savedAt: isoDateString,
  trigger: exploreBoardVersionTriggerSchema,
});
export type ExploreBoardVersion = z.infer<typeof exploreBoardVersionSchema>;

/** List shape for `GET /:id/versions` — preview computed server-side, like
 *  the board summary's cellsPreview (full snapshots stay in Firestore). */
export const exploreBoardVersionSummarySchema = z.object({
  id: z.string(),
  savedAt: isoDateString,
  trigger: exploreBoardVersionTriggerSchema,
  cellCount: z.number().int().nonnegative(),
  cellsPreview: z.array(exploreCellPreviewSchema).max(30).default([]),
});
export type ExploreBoardVersionSummary = z.infer<typeof exploreBoardVersionSummarySchema>;

/**
 * Board collections — `users/{email}/boardCollections/{id}`. A library grouping
 * on the home, orthogonal to pinned/favorite. A board points at one via
 * `board.collectionId` (absent/null = root). Brand-new subcollection, no legacy
 * data, so `createdAt`/`updatedAt` are required.
 */
export const exploreCollectionSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  order: z.number().int().min(0).default(0),
  createdAt: isoDateString,
  updatedAt: isoDateString,
});
export type ExploreCollection = z.infer<typeof exploreCollectionSchema>;

export const exploreCollectionCreateSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
});
export type ExploreCollectionCreate = z.infer<typeof exploreCollectionCreateSchema>;

export const exploreCollectionUpdateSchema = z
  .object({
    name: z.string().min(1).max(120),
    description: z.string().max(500),
    order: z.number().int().min(0),
  })
  .partial();
export type ExploreCollectionUpdate = z.infer<typeof exploreCollectionUpdateSchema>;

/**
 * User board templates — `users/{email}/boardTemplates/{id}`. A named snapshot
 * of a board's cells (+ tabs + board filters + palette, so an instantiated
 * template reproduces the dashboard, not just the layout). Cells reuse
 * `exploreCellSchema` (heals + clamps); the cached Atlas `insight` is stripped
 * at save time, and fresh cell ids are minted at instantiate time.
 */
export const exploreBoardTemplateSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  cells: z.array(exploreCellSchema).max(50).default([]),
  tabs: z.array(exploreBoardTabSchema).max(20).default([]),
  filters: exploreBoardFiltersSchema.optional(),
  defaultPalette: explorePaletteSchema.optional(),
  customPalette: z.array(hexColorSchema).min(1).max(12).optional(),
  createdAt: isoDateString,
});
export type ExploreBoardTemplate = z.infer<typeof exploreBoardTemplateSchema>;

export const exploreBoardTemplateCreateSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  cells: z.array(exploreCellSchema).max(50),
  tabs: z.array(exploreBoardTabSchema).max(20).optional(),
  filters: exploreBoardFiltersSchema.optional(),
  defaultPalette: explorePaletteSchema.optional(),
  customPalette: z.array(hexColorSchema).min(1).max(12).optional(),
});
export type ExploreBoardTemplateCreate = z.infer<typeof exploreBoardTemplateCreateSchema>;

/** List shape for `GET /explore-board-templates` — geometry-only previews; the
 *  full cells come from the detail endpoint at instantiate time. */
export const exploreBoardTemplateSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  cellCount: z.number().int().nonnegative(),
  createdAt: isoDateString,
  cellsPreview: z.array(exploreCellPreviewSchema).max(30).default([]),
});
export type ExploreBoardTemplateSummary = z.infer<typeof exploreBoardTemplateSummarySchema>;

/**
 * Board comments — `users/{email}/exploreBoards/{id}/comments/{cid}`.
 * Owner-scoped in v1: NEVER exposed on the `?token=` read-only path.
 * Flat list (no threads/reactions yet).
 */
export const exploreBoardCommentSchema = z.object({
  id: z.string().min(1).max(120),
  authorEmail: z.string().email(),
  authorName: z.string().max(120).optional(),
  text: z.string().min(1).max(2_000),
  /** Optional anchor — the cell this comment is about. A dangling id (cell
   *  later deleted) renders as "(deleted cell)" client-side, never blocks. */
  cellId: z.string().min(1).max(64).optional(),
  createdAt: isoDateString,
  updatedAt: isoDateString,
});
export type ExploreBoardComment = z.infer<typeof exploreBoardCommentSchema>;

export const exploreBoardCommentCreateSchema = z.object({
  text: z.string().min(1).max(2_000),
  cellId: z.string().min(1).max(64).optional(),
});
export type ExploreBoardCommentCreate = z.infer<typeof exploreBoardCommentCreateSchema>;

export const exploreBoardCommentUpdateSchema = z.object({
  text: z.string().min(1).max(2_000),
});
export type ExploreBoardCommentUpdate = z.infer<typeof exploreBoardCommentUpdateSchema>;

/** Response of `GET /metric-hub/embed-check?url=` (authoring-time advisory). */
export const embedCheckResponseSchema = z.object({
  embeddable: z.boolean(),
  title: z.string().max(300).nullable(),
});
export type EmbedCheckResponse = z.infer<typeof embedCheckResponseSchema>;
